import { useState } from 'react';
import { useMemberOptions } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { Field, MoneyInput } from './ui';
import { IconCheck, IconPlus, IconTrash } from './Icons';
import { NewReceiptModal } from './receipts/NewReceiptModal';
import { ReceiptDetailModal } from './receipts/ReceiptDetailModal';
import { TransferBadge } from './receipts/TransferBadge';
import { ClientAccount } from './leads/ClientAccount';
import { LeadPurchases } from './leads/LeadPurchases';
import { formatBRL, formatDate, LEVERAGE_LABELS, STATUS_LABELS } from '@/lib/format';
import { countsAsSold, hasOwnValue, isGas, notSoldHint, ownValueOf } from '@/lib/receipts';
import type { Lead, Receipt } from '@/lib/types';

interface LeadDetailProps {
  lead: Lead;
  onSaveField: (patch: Record<string, unknown>) => Promise<void>;
  onDelete: () => void;
}

/**
 * Painel expandido de um cliente: edição inline dos dados, as compras na
 * Ephra e os comprovantes vinculados, com "Anexar comprovante" já com este
 * cliente escolhido.
 */
export function LeadDetail({ lead, onSaveField, onDelete }: LeadDetailProps) {
  const { user, isAdmin } = useAuth();
  const members = useMemberOptions();
  /** Janela de lançamento aberta: comprovante comum ou já como gás. */
  const [attaching, setAttaching] = useState<'receipt' | 'gas' | null>(null);
  const [selected, setSelected] = useState<Receipt | null>(null);

  const canEdit = isAdmin || user?.id === lead.ownerId;

  return (
    <div
      style={{ background: 'var(--muted)', padding: '18px 20px', borderTop: '1px solid var(--border)' }}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="grid grid4" style={{ marginBottom: 14 }}>
        <Field label="Nome">
          <input
            className="inp"
            defaultValue={lead.name}
            disabled={!canEdit}
            onBlur={(event) => {
              const value = event.target.value.trim();
              if (value && value !== lead.name) void onSaveField({ name: value });
            }}
          />
        </Field>
        <Field label="E-mail">
          <input
            className="inp"
            type="email"
            defaultValue={lead.email ?? ''}
            disabled={!canEdit}
            onBlur={(event) => {
              const value = event.target.value.trim();
              if (value !== (lead.email ?? '')) void onSaveField({ email: value });
            }}
          />
        </Field>
        <Field label="Telefone">
          <input
            className="inp"
            defaultValue={lead.phone ?? ''}
            disabled={!canEdit}
            onBlur={(event) => {
              const value = event.target.value.trim();
              if (value !== (lead.phone ?? '')) void onSaveField({ phone: value });
            }}
          />
        </Field>
        <Field label="Status">
          <select
            className="inp"
            defaultValue={lead.status}
            disabled={!canEdit}
            onChange={(event) => void onSaveField({ status: event.target.value })}
          >
            {Object.entries(STATUS_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </Field>
      </div>

      <div className="grid grid3" style={{ marginBottom: 14 }}>
        <Field label="Data do cadastro">
          <input
            className="inp"
            type="date"
            defaultValue={lead.date}
            disabled={!canEdit}
            onChange={(event) => {
              if (event.target.value) void onSaveField({ date: event.target.value });
            }}
          />
        </Field>
        <Field label="Horário">
          <input
            className="inp"
            type="time"
            defaultValue={lead.time}
            disabled={!canEdit}
            onChange={(event) => {
              if (event.target.value) void onSaveField({ time: event.target.value });
            }}
          />
        </Field>
        <Field label="Responsável">
          {/* Trocar o responsável é decisão de administrador; vazio = "Sem responsável". */}
          <select
            className="inp"
            defaultValue={lead.ownerId ?? ''}
            disabled={!isAdmin}
            onChange={(event) => void onSaveField({ ownerId: event.target.value || null })}
          >
            {isAdmin ? (
              <>
                <option value="">Sem responsável</option>
                {members.map((member) => <option key={member.id} value={member.id}>{member.name}</option>)}
              </>
            ) : (
              <option value={lead.ownerId ?? ''}>{lead.ownerName ?? 'Sem responsável'}</option>
            )}
          </select>
        </Field>
      </div>

      <div className="grid grid2" style={{ marginBottom: 18 }}>
        <Field label="Lucro obtido (R$)">
          <LeadMoneyInput field="profit" label="lucro" value={lead.profit} disabled={!canEdit} onSave={onSaveField} />
        </Field>
        <Field
          label="Depositado na corretora (R$)"
          hint={lead.depositUpdatedAt ? `Atualizado em ${formatDate(lead.depositUpdatedAt)}` : 'Quanto o cliente tem lá hoje.'}
        >
          <LeadMoneyInput field="deposit" label="depósito" value={lead.deposit} disabled={!canEdit} onSave={onSaveField} />
        </Field>
      </div>

      <div style={{ marginBottom: 18 }}>
        <ClientAccount lead={lead} canEdit={canEdit} onSave={onSaveField} />
      </div>

      <Field label="Observações">
        <textarea
          className="inp"
          defaultValue={lead.notes ?? ''}
          disabled={!canEdit}
          placeholder="Anotações internas sobre o atendimento"
          onBlur={(event) => {
            const value = event.target.value.trim();
            if (value !== (lead.notes ?? '')) void onSaveField({ notes: value });
          }}
        />
      </Field>

      <div className="num" style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 18, fontSize: 13 }}>
        <span className="emphasis" style={{ fontWeight: 500 }}>Vendido: {formatBRL(lead.soldTotal)}</span>
        <span className="muted">em {lead.saleCount} {lead.saleCount === 1 ? 'venda' : 'vendas'}</span>
        {lead.gasCount > 0 ? (
          <>
            <span className="muted" aria-hidden>·</span>
            <span style={{ fontWeight: 500 }} title={notSoldHint('GAS')}>Gás: {formatBRL(lead.gasTotal)}</span>
            <span className="muted">em {lead.gasCount} {lead.gasCount === 1 ? 'lançamento' : 'lançamentos'}</span>
          </>
        ) : null}
      </div>

      <LeadPurchases purchases={lead.purchases} />

      <div style={{ fontSize: 13, fontWeight: 500, margin: '18px 0 8px' }}>
        Comprovantes vinculados <span className="muted" style={{ fontWeight: 400 }}>({lead.receiptCount})</span>
      </div>

      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden', background: 'var(--card)' }}>
        {lead.receipts.length === 0 ? (
          <div className="muted" style={{ padding: '12px 14px', fontSize: 13 }}>
            Nenhum comprovante vinculado a este cliente ainda.
          </div>
        ) : (
          lead.receipts.map((receipt) => (
            <button key={receipt.id} type="button" className="receipt-line" onClick={() => setSelected(receipt)}>
              <span className="num nowrap" style={{ width: 128 }}>
                {formatDate(receipt.date)} <span className="muted">{receipt.time}</span>
              </span>
              <span className="truncate" style={{ flex: '1 1 160px', minWidth: 0 }}>{receipt.description}</span>
              {receipt.leverageType ? <span className="chip">{LEVERAGE_LABELS[receipt.leverageType]}</span> : null}
              {isGas(receipt.leverageType) ? null : <TransferBadge receipt={receipt} long />}
              {countsAsSold(receipt.leverageType) ? (
                <span className="num emphasis nowrap" style={{ fontWeight: 500 }}>{formatBRL(receipt.soldAmount)}</span>
              ) : hasOwnValue(receipt.leverageType) ? (
                // Depósito, alavancagem e gás mostram o valor, mas na cor do texto: não é vendido.
                <span className="num nowrap" style={{ fontWeight: 500 }} title={notSoldHint(receipt.leverageType)}>{formatBRL(ownValueOf(receipt))}</span>
              ) : (
                <span className="num muted nowrap" title={notSoldHint(receipt.leverageType)}>—</span>
              )}
            </button>
          ))
        )}

        {canEdit ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, borderTop: '1px solid var(--border)', padding: '9px 14px' }}>
            <button type="button" className="btn" onClick={() => setAttaching('receipt')}>
              <IconPlus /> Anexar comprovante
            </button>
            <button type="button" className="btn" onClick={() => setAttaching('gas')}>
              <IconPlus /> Registrar gás
            </button>
          </div>
        ) : null}
      </div>

      {canEdit ? (
        <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-danger" onClick={onDelete}>
            <IconTrash /> Excluir cliente
          </button>
        </div>
      ) : (
        <div className="muted" style={{ fontSize: 11.5, marginTop: 12 }}>
          Você tem acesso de leitura a este cliente.
        </div>
      )}

      {attaching ? (
        <NewReceiptModal
          defaultLeadId={lead.id}
          defaultLeadName={lead.name}
          defaultMemberId={isAdmin ? lead.ownerId ?? undefined : undefined}
          {...(attaching === 'gas' ? { defaultType: 'GAS' as const } : {})}
          onClose={() => setAttaching(null)}
        />
      ) : null}
      {selected ? <ReceiptDetailModal receipt={selected} onClose={() => setSelected(null)} /> : null}
    </div>
  );
}

interface LeadMoneyInputProps {
  field: 'profit' | 'deposit';
  /** Nome curto do campo, para o botão de salvar. */
  label: string;
  value: number;
  disabled: boolean;
  onSave: (patch: Record<string, unknown>) => Promise<void>;
}

/** Valor em reais do cliente: o botão de salvar só aparece quando o número mudou. */
function LeadMoneyInput({ field, label, value, disabled, onSave }: LeadMoneyInputProps) {
  const [draft, setDraft] = useState(value);
  const save = () => {
    if (draft !== value) void onSave({ [field]: draft });
  };
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <MoneyInput value={draft} onChange={setDraft} disabled={disabled} onEnter={save} />
      {draft !== value ? (
        <button type="button" className="btn btn-primary btn-sm" onClick={save} title={`Salvar ${label}`}>
          <IconCheck />
        </button>
      ) : null}
    </div>
  );
}
